import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "./ui/button";
import { logError } from "../lib/logError";

// React error boundaries must be class components — there is no hook
// equivalent for componentDidCatch/getDerivedStateFromError. Used twice:
// once at the very root (frontend/src/index.js, no dependency on any app
// context so it survives even a provider-level crash) and once inside
// App.js wrapping the routed content (so an auth/cart-unrelated page crash
// doesn't take the whole app down). Same component both places.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    logError("ErrorBoundary", error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-brand-cream px-5" data-testid="error-boundary-screen">
          <div className="bg-white rounded-3xl border border-neutral-200 p-8 text-center max-w-md w-full">
            <div className="w-16 h-16 rounded-3xl bg-brand-clay/10 text-brand-clay flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8" strokeWidth={2.2} />
            </div>
            <h2 className="font-heading text-2xl font-extrabold mt-5">Something went wrong</h2>
            <p className="text-neutral-600 mt-2 leading-relaxed">
              Please try again. If the problem keeps happening, contact support.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="mt-6 h-14 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7 w-full"
              data-testid="error-boundary-reload-btn"
            >
              <RotateCw className="w-4 h-4 mr-2" /> Reload
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
